import { letterById, letters, mulberry32 } from "@repo/hindi-shared";
import { useEffect, useMemo, useRef, useState } from "react";

import { DualLabel } from "../ui/DualLabel";
import { GlyphTile } from "../ui/GlyphTile";
import { Shirorekha } from "../ui/Shirorekha";
import { SpeakerIcon } from "../ui/SpeakerIcon";

import { gradeFromChoice } from "./grading";
import { dateKey, hashSeed } from "./seed";
import type { ExerciseProps } from "./types";

/** Seeded anchor-consonant pool for variety across rounds (क ब म न स). */
const ANCHOR_CONSONANT_IDS = ["ka", "ba", "ma", "na", "sa"];
const GAP_COUNT = 3;
const DISTRACTOR_COUNT = 2;
const FLASH_MS = 450;
/** Tray sentinel for the inherent-a (no matra) form — never a real letter id. */
const BASE_FORM_TRAY_ID = "__base__";

/** Every matra id, in canonical `letters.ts` order (aa, i, ii, u, uu, ri, e, ai, o, au). */
const MATRA_IDS: string[] = letters
  .filter((letter) => letter.kind === "matra")
  .map((letter) => letter.id);

/** Fisher-Yates shuffle using an injected `[0, 1)` rand source. Non-mutating. */
function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy;
}

/** The consonant + matra glyph, e.g. ("ka", "matra-aa") -> "का". Null matraId -> inherent-a form. */
export function formGlyph(consonantId: string, matraId: string | null): string {
  const consonant = letterById.get(consonantId)?.glyph ?? "";
  if (matraId === null) return consonant;
  const matra = letterById.get(matraId)?.glyph ?? "";
  return `${consonant}${matra}`;
}

/** The fully-determined state of one Barakhadi round — exported for direct unit testing. */
export interface BarakhadiTarget {
  consonantId: string;
  /** One entry per row cell: base (matraId null) + 10 matra forms, in canonical order. */
  rowMatraIds: (string | null)[];
  /** Row indices (into `rowMatraIds`) left as gaps to fill. */
  gapIndices: number[];
  /** Tray: the gap forms (as tray ids — real matra ids or the base sentinel) plus distractor forms, shuffled. */
  trayIds: string[];
}

/**
 * Build one Barakhadi round: pick a seeded anchor consonant, 3 seeded gaps
 * in its row (always including `matraItemId`'s own cell), and a tray of the
 * 3 missing forms plus 2 same-consonant wrong-matra distractors.
 */
export function buildBarakhadiTarget(
  matraItemId: string,
  rand: () => number,
): BarakhadiTarget {
  const anchorIndex = Math.floor(rand() * ANCHOR_CONSONANT_IDS.length);
  const consonantId = ANCHOR_CONSONANT_IDS[anchorIndex]!;

  const rowMatraIds: (string | null)[] = [null, ...MATRA_IDS];

  // Always include the graded matra's row position among the gaps, so the
  // exercise actually drills the item it's meant to grade.
  const targetRowIndex = rowMatraIds.indexOf(matraItemId);
  const otherIndices = rowMatraIds
    .map((_, i) => i)
    .filter((i) => i !== targetRowIndex);
  const shuffledOthers = shuffle(otherIndices, rand);
  const extraGapCount = targetRowIndex === -1 ? GAP_COUNT : GAP_COUNT - 1;
  const gapIndices = [
    ...(targetRowIndex === -1 ? [] : [targetRowIndex]),
    ...shuffledOthers.slice(0, extraGapCount),
  ].sort((a, b) => a - b);

  const gapTrayIds = gapIndices.map((i) => rowMatraIds[i] ?? BASE_FORM_TRAY_ID);

  // Distractors: same consonant, wrong matras — drawn from matras NOT
  // currently gapped (every matra is used somewhere in the full row, so a
  // distractor may coincide with an already-visible non-gap cell; that's
  // fine, it's still a wrong answer for every open gap).
  const gappedMatraIds = new Set(gapIndices.map((i) => rowMatraIds[i]));
  const distractorPool = MATRA_IDS.filter((id) => !gappedMatraIds.has(id));
  const distractorTrayIds = shuffle(distractorPool, rand).slice(
    0,
    DISTRACTOR_COUNT,
  );

  const trayIds = shuffle([...gapTrayIds, ...distractorTrayIds], rand);

  return { consonantId, rowMatraIds, gapIndices, trayIds };
}

/**
 * Barakhadi — the consonant's full vowel-sign row (क का कि की कु कू कृ के
 * कै को कौ, in canonical matra order), for the matra family named by
 * `exercise.itemId`. A seeded anchor consonant (from क ब म न स, for
 * variety) gives 3 gaps in its row — always including the graded matra's
 * own cell — plus a tray of the 3 missing forms and 2 same-consonant wrong
 * forms. Clicking a tray tile fills the next open gap in row order; a
 * wrong pick flashes and counts as a mistake, a right pick fills
 * immediately. Grades the matra item (`exercise.itemId`), not the
 * consonant, per spec.
 */
export function Barakhadi({ exercise, audio, onComplete }: ExerciseProps) {
  const matraLetter = letterById.get(exercise.itemId);

  const target = useMemo(() => {
    const rand = mulberry32(hashSeed(exercise.itemId + dateKey()));
    return buildBarakhadiTarget(exercise.itemId, rand);
  }, [exercise.itemId]);

  const [filledIndices, setFilledIndices] = useState<Set<number>>(new Set());
  const [mistakes, setMistakes] = useState(0);
  const [wrongTrayId, setWrongTrayId] = useState<string | null>(null);
  const [placedTrayIds, setPlacedTrayIds] = useState<Set<string>>(new Set());

  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (matraLetter !== undefined) {
      void audio.speak(formGlyph(target.consonantId, matraLetter.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- speak once per mount
  }, []);

  if (matraLetter === undefined) return null;

  const nextGapIndex = target.gapIndices.find((i) => !filledIndices.has(i));

  function handleTrayClick(trayId: string): void {
    if (nextGapIndex === undefined) return; // already complete
    const expectedTrayId =
      target.rowMatraIds[nextGapIndex] ?? BASE_FORM_TRAY_ID;

    if (trayId !== expectedTrayId) {
      // A wrong pick anywhere across the whole row counts as one mistake —
      // `mistakes` tracks total wrong picks, not total clicks, so a
      // flawless 3-gap fill still grades gradeFromChoice(1) = Good (2).
      const nextMistakes = mistakes + 1;
      setMistakes(nextMistakes);
      setWrongTrayId(trayId);
      window.setTimeout(() => {
        if (cancelledRef.current) return;
        setWrongTrayId(null);
      }, FLASH_MS);
      return;
    }

    const isLastGap =
      target.gapIndices.filter((i) => !filledIndices.has(i)).length === 1;
    setFilledIndices((prev) => new Set(prev).add(nextGapIndex));
    setPlacedTrayIds((prev) => new Set(prev).add(trayId));

    if (isLastGap) {
      const grade = gradeFromChoice(mistakes + 1);
      window.setTimeout(() => {
        if (cancelledRef.current) return;
        onComplete({ grade, itemId: exercise.itemId });
      }, FLASH_MS);
    }
  }

  function handleReplay(): void {
    void audio.speak(formGlyph(target.consonantId, matraLetter!.id));
  }

  return (
    <div className="flex flex-col items-center gap-8 text-center">
      <p className="font-dev text-sm text-ink-soft uppercase">
        <DualLabel variant="inline" en="Barakhadi" hi="बाराखड़ी" />
      </p>
      <p className="-mt-4 font-latin text-sm text-ink-soft">
        <span lang="en">Listen, then tap the tiles to fill the row</span>
      </p>

      <button
        type="button"
        onClick={handleReplay}
        className="font-latin text-sm text-ink-soft underline"
      >
        <SpeakerIcon className="mr-1.5" />
        <DualLabel variant="inline" en="Replay" hi="फिर सुनो" />
      </button>

      <Shirorekha>
        <div className="flex flex-wrap justify-center gap-2 pb-1">
          {target.rowMatraIds.map((matraId, index) => {
            const isGap = target.gapIndices.includes(index);
            const isFilled = filledIndices.has(index);
            if (isGap && !isFilled) {
              return (
                <div
                  key={index}
                  className="flex h-16 w-16 items-center justify-center border border-dashed border-rule bg-paper text-ink-soft"
                />
              );
            }
            return (
              <GlyphTile
                key={index}
                glyph={formGlyph(target.consonantId, matraId)}
                size="md"
                state={isGap && isFilled ? "correct" : "idle"}
                disabled
              />
            );
          })}
        </div>
      </Shirorekha>

      <div className="flex flex-wrap justify-center gap-3">
        {target.trayIds.map((trayId, index) => {
          const isPlaced = placedTrayIds.has(trayId);
          if (isPlaced) return null;
          const matraId = trayId === BASE_FORM_TRAY_ID ? null : trayId;
          return (
            <GlyphTile
              key={`${trayId}-${index}`}
              glyph={formGlyph(target.consonantId, matraId)}
              size="md"
              state={wrongTrayId === trayId ? "wrong" : "idle"}
              onClick={() => handleTrayClick(trayId)}
            />
          );
        })}
      </div>
    </div>
  );
}
